/**
 * MoleculeStore - Manages a collection of loaded molecules
 *
 * Handles molecule list, selection, and navigation
 * Adapted from simple-viewer with VDisplay-specific extensions
 */

import type { MoleculeEntry, MoleculeChangeCallback, ListChangeCallback } from './types';

export class MoleculeStore {
  private molecules: MoleculeEntry[] = [];
  private currentIndex: number = -1;
  private changeCallbacks: MoleculeChangeCallback[] = [];
  private listChangeCallbacks: ListChangeCallback[] = [];
  private idCounter: number = 0;

  /**
   * Generate a unique ID for a molecule
   */
  private generateId(): string {
    return `mol_${++this.idCounter}_${Date.now()}`;
  }

  /**
   * Add a molecule to the store
   */
  add(entry: Omit<MoleculeEntry, 'id'>): MoleculeEntry {
    const molecule: MoleculeEntry = {
      ...entry,
      id: this.generateId(),
    };
    this.molecules.push(molecule);

    // Auto-select first molecule
    if (this.molecules.length === 1) {
      this.currentIndex = 0;
      this.notifyChange();
    }

    this.notifyListChange();
    return molecule;
  }

  /**
   * Add multiple molecules at once
   */
  addAll(entries: Omit<MoleculeEntry, 'id'>[]): MoleculeEntry[] {
    const added = entries.map(entry => ({
      ...entry,
      id: this.generateId(),
    }));

    this.molecules.push(...added);

    // Auto-select first molecule if none selected
    if (this.currentIndex === -1 && this.molecules.length > 0) {
      this.currentIndex = 0;
      this.notifyChange();
    }

    this.notifyListChange();
    return added;
  }

  /**
   * Select a molecule by index
   */
  select(index: number): void {
    if (index >= 0 && index < this.molecules.length) {
      this.currentIndex = index;
      this.notifyChange();
    }
  }

  /**
   * Select a molecule by ID
   */
  selectById(id: string): void {
    const index = this.molecules.findIndex(m => m.id === id);
    if (index !== -1) {
      this.select(index);
    }
  }

  /**
   * Get the currently selected molecule
   */
  getCurrent(): MoleculeEntry | null {
    if (this.currentIndex >= 0 && this.currentIndex < this.molecules.length) {
      return this.molecules[this.currentIndex];
    }
    return null;
  }

  /**
   * Get current index
   */
  getCurrentIndex(): number {
    return this.currentIndex;
  }

  /**
   * Navigate to next molecule
   */
  next(): void {
    if (this.molecules.length > 0) {
      this.select((this.currentIndex + 1) % this.molecules.length);
    }
  }

  /**
   * Navigate to previous molecule
   */
  prev(): void {
    if (this.molecules.length > 0) {
      this.select((this.currentIndex - 1 + this.molecules.length) % this.molecules.length);
    }
  }

  /**
   * Navigate to first molecule
   */
  first(): void {
    if (this.molecules.length > 0) {
      this.select(0);
    }
  }

  /**
   * Navigate to last molecule
   */
  last(): void {
    if (this.molecules.length > 0) {
      this.select(this.molecules.length - 1);
    }
  }

  /**
   * Get molecules in a range (for SDF range selection feature)
   */
  getByRange(start: number, end: number): MoleculeEntry[] {
    const startIdx = Math.max(0, start);
    const endIdx = Math.min(this.molecules.length, end);
    return this.molecules.slice(startIdx, endIdx);
  }

  /**
   * Find a molecule by name
   */
  findByName(name: string): MoleculeEntry | null {
    return this.molecules.find(m => m.name === name) || null;
  }

  /**
   * Find molecules by name pattern
   */
  findByPattern(pattern: string): MoleculeEntry[] {
    const regex = new RegExp(pattern, 'i');
    return this.molecules.filter(m => regex.test(m.name));
  }

  /**
   * Reorder a molecule in the list
   */
  reorder(fromIndex: number, toIndex: number): void {
    if (
      fromIndex < 0 ||
      fromIndex >= this.molecules.length ||
      toIndex < 0 ||
      toIndex >= this.molecules.length
    ) {
      return;
    }

    const [molecule] = this.molecules.splice(fromIndex, 1);
    this.molecules.splice(toIndex, 0, molecule);

    // Adjust current index if needed
    if (this.currentIndex === fromIndex) {
      this.currentIndex = toIndex;
    } else if (fromIndex < this.currentIndex && toIndex >= this.currentIndex) {
      this.currentIndex--;
    } else if (fromIndex > this.currentIndex && toIndex <= this.currentIndex) {
      this.currentIndex++;
    }

    this.notifyListChange();
  }

  /**
   * Remove a molecule by index
   */
  remove(index: number): void {
    if (index >= 0 && index < this.molecules.length) {
      this.molecules.splice(index, 1);

      // Adjust current index
      if (this.molecules.length === 0) {
        this.currentIndex = -1;
      } else if (this.currentIndex >= this.molecules.length) {
        this.currentIndex = this.molecules.length - 1;
      }

      this.notifyListChange();
      this.notifyChange();
    }
  }

  /**
   * Remove current molecule
   */
  removeCurrent(): void {
    if (this.currentIndex >= 0) {
      this.remove(this.currentIndex);
    }
  }

  /**
   * Remove molecule by ID
   */
  removeById(id: string): void {
    const index = this.molecules.findIndex(m => m.id === id);
    if (index !== -1) {
      this.remove(index);
    }
  }

  /**
   * Clear all molecules
   */
  clear(): void {
    this.molecules = [];
    this.currentIndex = -1;
    this.notifyListChange();
    this.notifyChange();
  }

  /**
   * Get all molecules
   */
  getAll(): MoleculeEntry[] {
    return [...this.molecules];
  }

  /**
   * Get molecule by index
   */
  get(index: number): MoleculeEntry | null {
    if (index >= 0 && index < this.molecules.length) {
      return this.molecules[index];
    }
    return null;
  }

  /**
   * Get molecule count
   */
  count(): number {
    return this.molecules.length;
  }

  /**
   * Check if store has molecules
   */
  isEmpty(): boolean {
    return this.molecules.length === 0;
  }

  /**
   * Subscribe to selection changes
   */
  onChange(callback: MoleculeChangeCallback): () => void {
    this.changeCallbacks.push(callback);
    return () => {
      const index = this.changeCallbacks.indexOf(callback);
      if (index !== -1) {
        this.changeCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to list changes (add/remove)
   */
  onListChange(callback: ListChangeCallback): () => void {
    this.listChangeCallbacks.push(callback);
    return () => {
      const index = this.listChangeCallbacks.indexOf(callback);
      if (index !== -1) {
        this.listChangeCallbacks.splice(index, 1);
      }
    };
  }

  private notifyChange(): void {
    const current = this.getCurrent();
    this.changeCallbacks.forEach(cb => cb(current, this.currentIndex));
  }

  private notifyListChange(): void {
    this.listChangeCallbacks.forEach(cb => cb(this.getAll()));
  }
}
